import { HTMLAttributes, ReactNode, useState } from 'react';
import { IconChevron } from './Icon';

// Omit the DOM `title` attribute (string-only) so our richer `title?: ReactNode`
// wins — otherwise the two intersect to `string & ReactNode` and a non-string
// title (e.g. DocumentHeader's composed node) fails to type-check.
type CardProps = Omit<HTMLAttributes<HTMLDivElement>, 'title'> & {
  title?: ReactNode;
  actions?: ReactNode;
  subtitle?: ReactNode;
  footer?: ReactNode;
  padding?: 'xs' | 'sm' | 'md' | 'lg';
  // Tighten the gap between the title row and the body (mb-2 vs mb-4).
  tightHeader?: boolean;
  // When set, the title becomes a toggle that hides/shows the body (with a
  // chevron). `defaultCollapsed` only seeds the initial state.
  collapsible?: boolean;
  defaultCollapsed?: boolean;
};

// Compact scale (was 10/12/16/20px): tighter card interiors system-wide.
// One notch tighter across the board (2026-08-25 "smaller offset around
// containers" request) — every Card in the system inherits this scale.
const PAD: Record<'xs' | 'sm' | 'md' | 'lg', string> = {
  xs: 'p-1.5',
  sm: 'p-2',
  md: 'p-2.5',
  lg: 'p-3',
};

export function Card({ title, actions, subtitle, footer, padding = 'md', tightHeader = false, className = '', collapsible = false, defaultCollapsed = false, children, ...rest }: CardProps) {
  const [collapsed, setCollapsed] = useState(collapsible ? defaultCollapsed : false);
  const open = !(collapsible && collapsed);

  // A text title is the card's heading (h2), so screen-reader users can jump
  // card to card; a composed title (DocumentHeader's number and badges) stays a
  // div. Tailwind's preflight resets h2 size, weight and margin: no visual change.
  const plainTitle = typeof title === 'string' || typeof title === 'number';
  const titleCls = 'text-title font-semibold text-primary-text tracking-tight';
  const Heading = plainTitle ? 'h2' : 'div';

  return (
    <div
      className={`min-w-0 bg-surface rounded-card border border-frame shadow-card ${PAD[padding]} ${className}`}
      {...rest}
    >
      {(title || actions) && (
        <div className={`flex items-start justify-between gap-4 ${open ? (tightHeader ? 'mb-1' : 'mb-2.5') : 'mb-0'}`}>
          {collapsible ? (
            <Heading className="m-0">
              <button
                type="button"
                onClick={() => setCollapsed((v) => !v)}
                aria-expanded={open}
                className="flex items-center gap-2 text-left -my-1 py-1 rounded-md hover:opacity-80 transition"
              >
                <span className={`inline-flex text-muted transition-transform ${collapsed ? '-rotate-90' : ''}`}>
                  <IconChevron size={14} />
                </span>
                <span>
                  <span className={`block ${titleCls}`}>{title}</span>
                  {subtitle && <span className="block text-meta text-secondary mt-0.5">{subtitle}</span>}
                </span>
              </button>
            </Heading>
          ) : (
            <div>
              <Heading className={titleCls}>{title}</Heading>
              {subtitle && <div className="text-meta text-secondary mt-0.5">{subtitle}</div>}
            </div>
          )}
          {actions && <div className="shrink-0">{actions}</div>}
        </div>
      )}
      {open && children}
      {open && footer && (
        <div className="mt-4 pt-3 border-t border-border-light text-meta text-muted">
          {footer}
        </div>
      )}
    </div>
  );
}
